import { useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import styles from "./index.module.scss";
import { Typography, useMediaQuery, useTheme } from "@material-ui/core";
import { useSelector } from "react-redux";
import { RootState } from "store";
import supportIconUrl from "assets/icons/supportIcon.svg";
import ArrowCrumb from "components/Icons/ArrowCrumb";
import { useRouteTitle } from "library/Routes";
import HelpButtonShape from "./helpButtonShape";
import { useLocation } from "react-router-dom";

export const getTransitionClasses = (
  transitionStyles: Record<string, string>,
  prefix = ""
) => ({
  appear: transitionStyles[`${prefix}-appear`],
  appearActive: transitionStyles[`${prefix}-appear-active`],
  appearDone: transitionStyles[`${prefix}-appear-done`],
  enter: transitionStyles[`${prefix}-enter`],
  enterActive: transitionStyles[`${prefix}-enter-active`],
  enterDone: transitionStyles[`${prefix}-enter-done`],
  exit: transitionStyles[`${prefix}-exit`],
  exitActive: transitionStyles[`${prefix}-exit-active`],
  exitDone: transitionStyles[`${prefix}-exit-done`]
});

interface FloatingHelpButtonProps {
  label?: string;
  panel?: {
    title: string;
    label: string;
    mail: string;
  };
}

function removeAccents(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function getRootClassByPath(pathname: string) {
  if (
    pathname.includes("/contrato") ||
    pathname.includes("/cadastro") ||
    pathname.includes("/passo") ||
    pathname.includes("/editar") ||
    pathname.includes("/dashboard")
  ) {
    return styles.flowOfSteps;
  }

  return "";
}

export default function FloatingHelpButton({
  label = "Ajuda?",
  panel = {
    title: "Precisa de ajuda?",
    label: "Dúvidas ou problemas técnicos? Entre em contato com",
    mail: "suporte_fundacao@sicredi.com.br"
  }
}: FloatingHelpButtonProps) {
  const [opened, setOpened] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const auth = useSelector((state: RootState) => state.auth);
  const perfilUserName = auth?.userData?.profile?.nome;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const routeTitle = useRouteTitle();
  const { pathname } = useLocation();

  const routeClass = getRootClassByPath(pathname);

  function buildEmailBody({
    userName,
    removeAccentFromBody = false
  }: {
    moduleName?: string;
    userName: string;
    removeAccentFromBody?: boolean;
  }) {
    const now = new Date();
    const date = now.toLocaleDateString("pt-BR");
    const time = now.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    });

    const body = [
      "Olá,",
      `Para abertura do chamado, informe os dados abaixo:`,
      "CPF:",
      "Função que desempenha:",
      "Descreva o problema enfrentado:",
      "",
      "",
      "",
      "Anexe imagens do problema (print da tela completa).",
      "",
      `Data e Hora: ${date} - ${time}`,
      "Obrigado!",
      userName
    ].join("\r\n");

    return removeAccentFromBody ? removeAccents(body) : body;
  }

  function buildSupportEmailMailto({
    moduleName,
    userName
  }: {
    moduleName?: string;
    userName: string;
  }) {
    const supportEmail = "suporte_fundacao@sicredi.com.br";

    const subject = `[Programas Educacionais] - Suporte - ${moduleName ?? ""} - ${userName}`;

    const body = buildEmailBody({
      moduleName,
      userName,
      removeAccentFromBody: true
    });

    return `mailto:${supportEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }

  function buildSupportEmailOutlookWeb({
    moduleName,
    userName
  }: {
    moduleName?: string;
    userName: string;
  }) {
    const supportEmail = "suporte_fundacao@sicredi.com.br";

    const subject = `[Programas Educacionais] - Suporte - ${moduleName} - ${userName}`;

    const body = buildEmailBody({
      moduleName,
      userName,
      removeAccentFromBody: false
    });

    return `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(
      supportEmail
    )}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  function handleSupportClick() {
    const moduleName = routeTitle ?? "";
    const userName = perfilUserName ?? "";

    if (isMobile) {
      const mailto = buildSupportEmailMailto({
        moduleName,
        userName
      });

      window.location.href = mailto;
    } else {
      const outlookUrl = buildSupportEmailOutlookWeb({
        moduleName,
        userName
      });

      window.open(outlookUrl, "_blank", "noopener,noreferrer");
    }

    setOpened(false);
  }

  return (
    <div className={`${styles.root} ${routeClass}`.trim()} ref={wrapperRef}>
      <CSSTransition
        appear
        in={opened}
        timeout={250}
        classNames={getTransitionClasses(styles, "root")}
        mountOnEnter
        unmountOnExit
      >
        <div
          className={styles.panel}
          onClick={handleSupportClick}
        >
          <div className={styles.texts}>
            <Typography style={{ color: "#33820D", lineHeight: "1", fontSize: 20 }}>
              {panel.title}
            </Typography>

            <Typography
              variant="caption"
            >
              {panel.label}
            </Typography>

            <Typography
              variant="caption"
              style={{ fontWeight: "bold", margin: "-5px 0" }}
            >
              {panel.mail}
            </Typography>
          </div>

          <div className={styles.chev}>
            <ArrowCrumb color="#33820D" />
          </div>
        </div>
      </CSSTransition>

      <div
        className={styles.button}
        onClick={() => {
          setOpened((prev) => !prev);
        }}
      >
        <label>{label}</label>
        <img src={supportIconUrl} className={styles.icon} alt="" />
        <HelpButtonShape className={styles.shape} data-opened={opened} />
      </div>
    </div>
  );
}
